
import BaseDateInput from "@/components/base/BaseDateInput.vue";
import BaseDropdown from "@/components/base/BaseDropdown.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import usePassengers from "@/composables/usePassengers";
import { titles } from "@/config/titles";
import { parseDate } from "@/helpers/dateHelper";
import { DirectionEnum } from "@/models/enums";
import { PassengerState } from "@/models/www/store/BookingState";
import { useBookingStore } from "@/store/booking";
import useVuelidate from "@vuelidate/core";
import { helpers, minLength, required, requiredIf } from "@vuelidate/validators";
import { defineComponent, computed, PropType, reactive, watch } from "vue";

export default defineComponent({
	components: {
		BaseInput,
		BaseDateInput,
		BaseDropdown,
	},
	props: {
		index: {
			type: Number,
			required: true,
		},
		passenger: {
			type: Object as PropType<PassengerState>,
			required: true,
		},
		isTravelClub: {
			type: Boolean,
			required: false
		}
	},
	setup(props) {
		const bookingStore = useBookingStore();
		const { getPassengers } = usePassengers();
		const passengerTypes = getPassengers();
		const concessionNumberLabel = props.isTravelClub ? "Travel club number" : "Customer number";		

		const passengerText = computed(() => {
			const passengerType = passengerTypes.find(
				(t) => t.type === props.passenger.type
			);
			return `Passenger ${props.index + 1} ${passengerType?.name} ${
				passengerType?.ageSuffix
			}`;
		});
		
		const isAmending = bookingStore.getBooking.amendmentMode;
		
		const isLocked = bookingStore.getBooking.outward.isLocked;
		
		const passenger = computed(() => {
			return bookingStore
				.getPassengers(DirectionEnum.Outward)
				.find((x) => x.nameListId === props.passenger.nameListId);
		});

		const dateOfBirthIsValid = (value: string) => {
			const departureDate =
				bookingStore.getDate(DirectionEnum.Outward) ?? new Date();

			if (value.length !== 10) return false;
			const birthDate = parseDate(value, "dd/MM/yyyy");

			var diff = Math.abs(
				departureDate.getFullYear() - birthDate.getFullYear()
			);
			// Check if date has passed
			var m = departureDate.getMonth() - birthDate.getMonth();
			if (m < 0 || (m === 0 && departureDate.getDate() < birthDate.getDate())) {
				diff--;
			}

			const passengerRules = passengerTypes.find(
				(p) => p.type === props.passenger.type
			);

			return (
				diff >= (passengerRules?.min ?? 0) &&
				diff <= (passengerRules?.max ?? 150)
			);
		};
	
		const state = reactive({
			title: passenger.value?.title ?? "",
			firstName: passenger.value?.firstName ?? "",
			lastName: passenger.value?.lastName ?? "",
			dateOfBirth: passenger.value?.dateOfBirth ?? "",
			concessionNumber: passenger.value?.concessionNumber ?? ""
		});

		const rules = {
			title: { required },
			firstName: { required },
			lastName: { required },
			dateOfBirth: {
				required,
				min: minLength(10),
				valid: helpers.withMessage(
					() => "Birth date and passenger type do not match",
					dateOfBirthIsValid as any
				),
			},
			concessionNumber: {
				valid: helpers.withMessage("Value is required",
				requiredIf(() => props.passenger.validationRequired === true))
			}
		};

		watch(state, () => {
			if (props.passenger.nameListId) {
				bookingStore.updatePassenger(DirectionEnum.Outward, {
					...state,
					type: props.passenger.type,
					nameListId: props.passenger.nameListId,
					validationRequired: props.passenger.validationRequired,
				} as PassengerState);
			}
		});

		const v$ = useVuelidate(rules, state);
		
		return { titles, v$, passengerText, isAmending, isLocked, concessionNumberLabel };
	},
});
